import React, { useEffect, useState } from 'react';
import api from '../services/api';

const COLUMNS = [
  { key: 'new',                 label: 'New',                 color: '#4A7EB5', icon: '📥' },
  { key: 'contacted',           label: 'Contacted',           color: '#8B5CF6', icon: '📞' },
  { key: 'interview_scheduled', label: 'Interview Scheduled', color: '#F59E0B', icon: '📅' },
  { key: 'hired',               label: 'Hired',               color: '#10B981', icon: '🎉' },
  { key: 'no_hire',             label: 'No Hire',             color: '#EF4444', icon: '✕'  },
];

const SOURCE_META: Record<string, { label: string; color: string }> = {
  facebook:  { label: 'Facebook',  color: '#3B82F6' },
  instagram: { label: 'Instagram', color: '#EC4899' },
  referral:  { label: 'Referral',  color: '#10B981' },
  indeed:    { label: 'Indeed',    color: '#F59E0B' },
  other:     { label: 'Other',     color: '#8FA8C0' },
};

const getDays = (d: string) => {
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  return days === 0 ? 'Today' : `${days}d`;
};

export default function HiringPipelinePage() {
  const [leads, setLeads]       = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [moving, setMoving]     = useState<string | null>(null);
  const [search, setSearch]     = useState('');

  // Notes modal
  const [notesLead, setNotesLead]   = useState<any>(null);
  const [editNotes, setEditNotes]   = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchLeads = async () => {
    try {
      const r = await api.get('/hiring-leads');
      setLeads(r.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLeads(); }, []);

  const moveToStatus = async (id: string, status: string, _source: string) => {
    setMoving(id);
    try {
      await api.patch(`/hiring-leads/${id}`, { status, _source });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    } catch (e) { console.error(e); }
    finally { setMoving(null); }
  };

  const saveNotes = async () => {
    if (!notesLead) return;
    setSavingNotes(true);
    try {
      await api.patch(`/hiring-leads/${notesLead.id}`, { notes: editNotes, _source: notesLead._source });
      setLeads(prev => prev.map(l => l.id === notesLead.id ? { ...l, notes: editNotes } : l));
      setNotesLead(null);
    } catch (e) { console.error(e); }
    finally { setSavingNotes(false); }
  };

  const filtered = leads.filter(l =>
    !search ||
    l.name?.toLowerCase().includes(search.toLowerCase()) ||
    l.phone?.includes(search) ||
    l.email?.toLowerCase().includes(search.toLowerCase())
  );

  const colLeads = (key: string) => filtered.filter(l => l.status === key);

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0 }}>💼 Hiring Pipeline</h1>
          <div style={{ color: '#8FA8C0', fontSize: 13 }}>
            {leads.length} candidates · {leads.filter(l => l.status === 'hired').length} hired
          </div>
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search by name, phone, or email..."
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 10,
            background: '#1A2B3C', border: '1px solid #2D4259',
            color: '#fff', fontSize: 14, boxSizing: 'border-box' as const,
          }}
        />
      </div>

      {/* Kanban Board */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', padding: '0 24px 24px' }}>
        <div style={{ display: 'flex', gap: 12, height: '100%', alignItems: 'flex-start' }}>
          {COLUMNS.map(col => {
            const cards = colLeads(col.key);
            return (
              <div key={col.key} style={{
                width: 260, flexShrink: 0,
                background: '#1A2B3C', borderRadius: 14,
                border: '1px solid #2D4259',
                borderTop: `3px solid ${col.color}`,
                display: 'flex', flexDirection: 'column',
                maxHeight: 'calc(100vh - 180px)',
              }}>
                {/* Column Header */}
                <div style={{ padding: '12px 14px', borderBottom: '1px solid #2D4259', flexShrink: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{col.icon} {col.label}</span>
                    <span style={{ background: col.color, color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>
                      {cards.length}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
                  {loading ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#4A6580', fontSize: 12 }}>Loading...</div>
                  ) : cards.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: '#4A6580', fontSize: 12, border: '1px dashed #2D4259', borderRadius: 8 }}>
                      Empty
                    </div>
                  ) : cards.map(lead => {
                    const src = SOURCE_META[lead.source] || SOURCE_META.other;
                    const days = getDays(lead.created_at);
                    const isMoving = moving === lead.id;

                    return (
                      <div
                        key={lead.id}
                        style={{
                          background: '#243447', borderRadius: 10,
                          padding: 12, marginBottom: 8,
                          border: '1px solid #2D4259',
                          cursor: 'default', transition: 'border-color 0.15s',
                          opacity: isMoving ? 0.5 : 1,
                        }}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = col.color)}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = '#2D4259')}
                      >
                        {/* Name + source */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                          <div style={{ fontWeight: 700, color: '#fff', fontSize: 13 }}>{lead.name}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                            <span style={{ background: src.color + '22', color: src.color, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{src.label}</span>
                            {lead._source === 'lead' && <span style={{ background: '#10B98122', color: '#10B981', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4 }}>Auto</span>}
                          </div>
                        </div>

                        {/* Contact */}
                        {lead.phone && <div style={{ color: '#4A7EB5', fontSize: 11, marginBottom: 2 }}>📞 {lead.phone}</div>}
                        {lead.email && <div style={{ color: '#8FA8C0', fontSize: 11, marginBottom: 4 }}>✉️ {lead.email}</div>}

                        {/* Notes */}
                        {lead.notes && (
                          <div style={{ color: '#8FA8C0', fontSize: 11, marginBottom: 6, lineHeight: 1.5, background: '#0F1923', borderRadius: 6, padding: '5px 8px' }}>
                            {lead.notes.slice(0, 80)}{lead.notes.length > 80 ? '...' : ''}
                          </div>
                        )}

                        {/* Footer */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, marginBottom: 8 }}>
                          <span style={{ color: '#4A6580', fontSize: 11 }}>{lead.position ? `Hiring - ${lead.position.charAt(0).toUpperCase() + lead.position.slice(1)}` : 'Hiring'}</span>
                          <span style={{ color: '#4A6580', fontSize: 11 }}>{days}</span>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 6 }}>
                          {/* Move forward */}
                          {col.key !== 'hired' && col.key !== 'no_hire' && (() => {
                            const nextIndex = COLUMNS.findIndex(c => c.key === col.key) + 1;
                            const next = COLUMNS[nextIndex];
                            if (!next || next.key === 'no_hire') return null;
                            return (
                              <button onClick={() => moveToStatus(lead.id, next.key, lead._source)} style={{
                                flex: 1, background: next.color + '22', color: next.color,
                                border: `1px solid ${next.color}44`, borderRadius: 7,
                                padding: '5px 0', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                              }}>
                                → {next.label}
                              </button>
                            );
                          })()}

                          {/* Hire from interview */}
                          {col.key === 'interview_scheduled' && (
                            <button onClick={() => moveToStatus(lead.id, 'hired', lead._source)} style={{
                              flex: 1, background: '#10B98122', color: '#10B981',
                              border: '1px solid #10B98144', borderRadius: 7,
                              padding: '5px 0', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            }}>
                              🎉 Hire
                            </button>
                          )}

                          {/* Notes */}
                          <button onClick={() => { setNotesLead(lead); setEditNotes(lead.notes || ''); }} style={{
                            background: 'transparent', color: '#8FA8C0',
                            border: '1px solid #2D4259', borderRadius: 7,
                            padding: '5px 8px', fontSize: 11, cursor: 'pointer',
                          }}>📝</button>

                          {/* No Hire */}
                          {col.key !== 'no_hire' && col.key !== 'hired' && (
                            <button onClick={() => moveToStatus(lead.id, 'no_hire', lead._source)} style={{
                              background: 'transparent', color: '#EF4444',
                              border: '1px solid #EF444433', borderRadius: 7,
                              padding: '5px 8px', fontSize: 11, cursor: 'pointer',
                            }}>✕</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {/* Notes Modal */}
    {notesLead && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={() => setNotesLead(null)}>
        <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 28, width: 400, border: '1px solid #2D4259' }}
          onClick={e => e.stopPropagation()}>
          <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 800, marginBottom: 4 }}>📝 Notes</h2>
          <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 16 }}>{notesLead.name}</p>
          <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={5}
            placeholder="Interview notes, observations, follow-up reminders..."
            style={{ width: '100%', background: '#0F1923', border: '1px solid #2D4259', color: '#fff', padding: '12px 14px', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' as const, resize: 'vertical' as const, marginBottom: 16 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setNotesLead(null)} style={{ flex: 1, background: '#243447', color: '#8FA8C0', border: '1px solid #2D4259', borderRadius: 10, padding: '11px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
            <button onClick={saveNotes} disabled={savingNotes} style={{ flex: 2, background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 10, padding: '11px', fontWeight: 800, fontSize: 14, cursor: 'pointer', opacity: savingNotes ? 0.6 : 1 }}>
              {savingNotes ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
